import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { usePermissions } from "@/hooks/usePermissions";
import { useRestaurantTablesStatus } from "@/hooks/useHasRestaurantTables";
import { APP_HOME_PATH } from "@/lib/copilot-home";

// Gates a route to organization admins (and super admins). Data isolation is
// enforced server-side (RLS + edge-function scoping); this guard only keeps the
// admin-only UI out of non-admins' hands.
export function AdminGuard({ orPermission, orPermissionNeedsTables = false }: { orPermission?: string; orPermissionNeedsTables?: boolean } = {}) {
  const { userRole } = useAuth();
  const { isSuperAdmin, loading } = useSuperAdmin();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const tables = useRestaurantTablesStatus(!!orPermission && orPermissionNeedsTables);

  const isAdmin = userRole === "admin" || isSuperAdmin;

  // Render nothing while checking — avoids an "access denied" flash mid-transition.
  if (loading) return null;
  if (!isAdmin && orPermission && (permissionsLoading || tables.loading)) return null;

  const allowedByPermission = !!orPermission
    && !!permissions?.has(orPermission)
    && (!orPermissionNeedsTables || tables.hasTables);
  if (!isAdmin && !allowedByPermission) {
    return <Navigate to={APP_HOME_PATH} replace />;
  }

  return <Outlet />;
}
